/**
 * @file ListSecurityRoles.js
 * @description This component is responsible for displaying all current security roles with functionalities for managing these roles.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    fetchSecurityRoles,
    deleteSecurityRoles,
    toggleRoleStatus,
    createSecurityRole,
} from '../../services/securityRoleService';
import styles from './security-roles.module.scss';
import Spinner from '../ui/spinners/Spinner';
import { getNameForDuplicate } from '../../utils/utils';
import { useTableSort, SortDirections } from '../../hooks/useTableSort';
import { useAuthContext } from '../../store/AuthContext';
import AlertBox from '../ui/no_data_indicators/AlertBox';
import {
    hasAddEditPermission,
} from './SecurityRolePermission';
import './ListSecurityRoles.scss';
import '../page-list/PageList.css';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const ListSecurityRoles = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const { hasSecurityAttribute, multitenantFlag } = useAuthContext();
    const [isUnauthorized, setUnauthorized] = useState(false);

    const { sortConfig, renderSortArrow } = useTableSort(
        'name',
        SortDirections.DESCENDING,
    );

    const selectAllRef = useRef(null);

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            const fetchedRoles = await fetchSecurityRoles(multitenantFlag);
            setRoles(fetchedRoles);
        } catch (error) {
            if (error.status === 400) {
                setUnauthorized(true);
            }
            console.error('Failed to fetch roles', error);
            Toast.error(getText('msg_6240caf7d347'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                selectedRoles.length > 0 && selectedRoles.length < roles.length;
        }
    }, [selectedRoles, roles]);

    const sortedRoles = roles.sort((a, b) => {
        if (!sortConfig.key) return 0;
        if (a[sortConfig.key] < b[sortConfig.key])
            return sortConfig.direction === SortDirections.ASCENDING ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
            return sortConfig.direction === SortDirections.ASCENDING ? 1 : -1;
        return 0;
    });

    const handleDelete = async () => {
        if (!canDeleteSecurityRoles()) {
            Toast.error(
                getText('msg_738eca28c9da'),
            );
            return false;
        }

        try {
            await deleteSecurityRoles(selectedRoles);
            Toast.success(getText('msg_247e088ef442'));
            const updatedRoles = roles.filter(
                role => !selectedRoles.includes(role.id),
            );
            setRoles(updatedRoles);
            setSelectedRoles([]);
        } catch (error) {
            console.error('Failed to delete roles', error);
            Toast.error(getText('msg_10631f38b7e3'));
        }
    };

    const canDeleteSecurityRoles = () => {
        var returnValue = true;

        selectedRoles.forEach(securityRoleId => {
            const role = roles.find(role => securityRoleId === role.id);

            if (!role) returnValue = false;

            if (!role.isActive || role.userCount > 0) returnValue = false;
        });

        return returnValue;
    };

    const openDeleteConfirmationModal = () => {
        if (!selectedRoles.length) {
            Toast.error(getText('msg_c8ccc5b97e0d'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            onYes: () => handleDelete(),
            onNo: () => { },
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Security roles delete clicked`);
    };

    const handleDuplicate = async () => {
        try {
            const roleNames = [];
            roles.forEach(role => roleNames.push(role.name));

            var securityRoleId = selectedRoles[0];

            const securityRole = roles.find(role => role.id === securityRoleId);
            const newName = getNameForDuplicate(securityRole.name, roleNames);
            const newRole = {
                ...securityRole,
                id: 0,
                name: newName,
            };
            roleNames.push(newName);

            const duplicatedRole = await createSecurityRole(newRole);
            duplicatedRole.userCount = 0;

            setRoles([...roles, duplicatedRole]);

            Toast.success(getText('msg_2962c1d304e5'));
        } catch (error) {
            console.error('Failed to duplicate role', error);
            Toast.error(getText('msg_749436667234'));
        }
    };

    const openDuplicateConfirmationModal = () => {
        if (!selectedRoles.length) {
            Toast.error(getText('msg_812ee7e3ed76'));
            return;
        }

        if (selectedRoles.length !== 1) {
            Toast.error(getText('msg_7f7a02abc528'));
            return;
        }

        const [id] = selectedRoles;
        const roleName = roles.find(role => role.id === id).name;

        showModal({
            type: ModalTypes.ACTION,
            title: `Are you sure you want to duplicate ${roleName}?`,
            onYes: () => handleDuplicate(),
            onNo: () => { },
            noButtonText: getText('msg_816c52fd2bdd'),
            yesButtonText: getText('msg_972d57379db3'),
        });
    };

    const handleToggleRoleStatus = async roleId => {
        try {
            await toggleRoleStatus(roleId);
            loadRoles();
            Toast.success(getText('msg_c8a86443c1f9'));
        } catch (error) {
            console.error('Failed to toggle role status', error);
            Toast.error(getText('msg_d89ae5602681'));
        }
    };

    const handleRoleSelection = roleId => {
        setSelectedRoles(prevSelection =>
            prevSelection.includes(roleId)
                ? prevSelection.filter(id => id !== roleId)
                : [...prevSelection, roleId],
        );
    };

    const handleSelectAllChange = event => {
        setSelectedRoles(
            event.target.checked ? roles.map(role => role.id) : [],
        );
    };

    const handleViewUsers = id => {
        navigate(ROUTE_BUILDERS.securityRolesView(id));
    };
    const handleViewSecurityGroup = id => {
        navigate(ROUTE_BUILDERS.securityRolesEdit(id));
    };

    return (
        <div className="list-security-roles-container page-content">
            {isUnauthorized ? (
                <>
                    <AlertBox
                        title={getText('msg_53cc4f43d8a7')}
                        description={getText('msg_7de2a9f3cc6c')}
                    />
                </>
            ) : (
                <div className="card">
                    <div className="card-body px-0 py-4 b">
                        <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                            <div className="float-start d-flex align-items-center">
                                <div>
                                    <p className="page-title card-title ms-4 me-5">
                                        {getText('msg_2544899993ff')}
                                    </p>
                                    <p
                                        className="ing-portals-subtitle mt-1"
                                        style={{ marginTop: '10px' }}
                                    >
                                        {getText('msg_7523e7cd10ca')}
                                    </p>
                                </div>
                            </div>
                            {hasAddEditPermission(
                                multitenantFlag,
                                hasSecurityAttribute
                            ) && (
                                    <div className="float-end d-flex align-items-center">
                                        <span
                                            type="button"
                                            onClick={openDeleteConfirmationModal}
                                            className="ing-text-buttons mx-3"
                                        >
                                            <i className="bi bi-trash3 me-2" />
                                            {getText('msg_f6fdbe48dc54')}
                                        </span>
                                        <span
                                            type="button"
                                            onClick={openDuplicateConfirmationModal}
                                            className="ing-text-buttons mx-3"
                                        >
                                            <i className="bi bi-copy me-2" />
                                            {getText('msg_972d57379db3')}
                                        </span>
                                        <div to="new-portal-page">
                                            <span
                                                onClick={() =>
                                                    navigate(
                                                        ROUTES.SECURITY_ROLES_ADD,
                                                    )
                                                }
                                                type="button"
                                                className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                            >
                                                <i className="bi bi-plus-lg me-1" />
                                                {getText('msg_61cc55aa0453')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                        </div>

                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : (
                            <div className="table-responsive-xxl w-100">
                                <table className="table table-hover align-middle ing-table-striped">
                                    <thead className="custom-table-header table-light">
                                        <tr>
                                            <th
                                                className={`text-center checkbox-column-head`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className={`form-check-input`}
                                                    ref={selectAllRef}
                                                    checked={
                                                        selectedRoles.length ===
                                                        roles.length &&
                                                        roles.length > 0
                                                    }
                                                    onChange={
                                                        handleSelectAllChange
                                                    }
                                                />
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText('msg_709a23220f2c')}
                                                </span>
                                                {renderSortArrow('name')}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText('msg_55f8ebc805e6')}
                                                </span>
                                                {renderSortArrow('description')}
                                            </th>
                                            <th className="text-end">
                                                <span className="table-column-header-text">
                                                    {getText('msg_66e12969c225')}
                                                </span>
                                                {renderSortArrow('userCount')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedRoles.map(role => (
                                            <tr key={role.id}>
                                                <td className="text-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={selectedRoles.includes(
                                                            role.id,
                                                        )}
                                                        onChange={() =>
                                                            handleRoleSelection(
                                                                role.id,
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {role.isActive && (
                                                    <td
                                                        className="pointer-cursor titleCell"
                                                        onClick={() =>
                                                            handleViewSecurityGroup(
                                                                role.id,
                                                            )
                                                        }
                                                    >
                                                        {role.name}
                                                    </td>
                                                )}

                                                {!role.isActive && (
                                                    <td className="titleCell">
                                                        {role.name}
                                                    </td>
                                                )}

                                                <td className="subtitleCell">
                                                    {role.description}
                                                </td>
                                                <td className="subtitleCell text-end">
                                                    <span
                                                        style={{
                                                            color: '#1366FF',
                                                            textDecoration:
                                                                'underline',
                                                            cursor: role.isActive
                                                                ? 'pointer'
                                                                : 'default',
                                                        }}
                                                        className="list-security-role-table-count-value"
                                                        onClick={() => {
                                                            if (role.isActive)
                                                                handleViewUsers(
                                                                    role.id,
                                                                    role.name,
                                                                );
                                                        }}
                                                    >
                                                        {role.userCount}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ListSecurityRoles;
